import axios from "axios";
import { formatApiError } from "./errors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("ren_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// -----------------------------------------------------------------------------
// 401 / session-expiry handling (production stabilization phase — Feb 2026)
// -----------------------------------------------------------------------------
// Before this rewrite the interceptor ONLY handled impersonation 401s; every
// other 401 propagated to the calling component, which typically rendered
// nothing → the dashboard would silently blank out after the token went bad.
//
// The new contract:
//   1. ANY non-OK 401 → clear local auth state and broadcast an
//      `auth:session-expired` window event. The AuthProvider listens and
//      shows a controlled "Session expired" modal instead of blanking the UI.
//   2. Impersonation 401 is still handled separately so the operator is
//      bounced back to `/internal-admin/tenants` with their super-admin token.
//   3. Multi-tab sync — clearing `ren_token` triggers a `storage` event in
//      sibling tabs; the AuthProvider treats that as a remote logout too.
//   4. Idempotent — we keep a single-flight `_handling401` flag so 5 parallel
//      failed requests don't trigger 5 modals.
// -----------------------------------------------------------------------------
let _impersonationRedirecting = false;
let _handling401 = false;

// Endpoints we deliberately allow to 401 quietly (e.g. unauthenticated probes
// against `/auth/me` from the very-first page load). Anything else gets the
// session-expired treatment.
const SILENT_401_PATHS = new Set([
  "/auth/me",
  "/auth/login",
  "/auth/register",
  "/auth/verify-email",
  "/auth/send-verification",
  "/auth/forgot-password",
  "/auth/reset-with-code",
]);

function _broadcastSessionExpired(reason) {
  if (typeof window === "undefined") return;
  if (_handling401) return;
  _handling401 = true;
  // Clear the bad token so nothing else can use it.
  try {
    localStorage.removeItem("ren_token");
  } catch (_) { /* ignore */ }
  try {
    window.dispatchEvent(
      new CustomEvent("auth:session-expired", { detail: { reason } })
    );
  } catch (_) { /* CustomEvent might not exist in very old browsers */ }
  // Reset the flag after a short cooldown so subsequent genuine logout +
  // re-login flows still work.
  setTimeout(() => { _handling401 = false; }, 2000);
}

api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    // Defensive normalization — attach a guaranteed-string `safeMessage`
    // to EVERY rejected error before it reaches any UI code so a future
    // `toast.error(err.safeMessage)` (or any JSX render of the message)
    // can never crash React with "Objects are not valid as a React child".
    try { err.safeMessage = formatApiError(err); } catch (_) { /* ignore */ }

    const status = err?.response?.status;
    const cfg = err?.config || {};
    const urlPath = (() => {
      try {
        // axios sets `cfg.url` to the path passed in (e.g. "/auth/me") so we
        // don't need to URL.parse — keep it cheap.
        return (cfg.url || "").split("?")[0];
      } catch (_) { return ""; }
    })();

    if (status === 401 && typeof window !== "undefined") {
      const backup = localStorage.getItem("sa_token_backup");
      const impCompany = localStorage.getItem("impersonation_company");
      const isImpersonationSession = !!(backup && impCompany);
      const reason = err?.response?.data?.code || err?.response?.data?.detail || "unauthorized";

      // --- Impersonation 401 → bounce back to super-admin -------------------
      if (isImpersonationSession && !_impersonationRedirecting) {
        _impersonationRedirecting = true;
        const company = impCompany || "the tenant";
        try {
          // eslint-disable-next-line global-require
          const { toast } = require("sonner");
          toast.error(
            `Impersonation session expired for ${company}. Your work was not lost — please re-impersonate from the Tenants page.`,
            { duration: 8000 }
          );
        } catch (_) { /* sonner not available — ignore */ }
        localStorage.setItem("ren_token", backup);
        localStorage.removeItem("sa_token_backup");
        localStorage.removeItem("impersonation_company");
        setTimeout(() => { window.location.replace("/internal-admin/tenants"); }, 300);
        return Promise.reject(err);
      }

      // --- Normal session 401 → broadcast + show modal ----------------------
      // Skip the broadcast for unauthenticated probes — those are expected
      // to 401 (e.g. an anonymous visitor hitting /auth/me).
      const hadToken = !!localStorage.getItem("ren_token");
      const silent = SILENT_401_PATHS.has(urlPath);
      if (hadToken && !silent) {
        // Lightweight diagnostic — picked up by browser devtools when users
        // share traces. Never affects production behavior.
        try {
          // eslint-disable-next-line no-console
          console.warn(
            `[auth] session-expired url=${urlPath} reason=${reason}`
          );
        } catch (_) { /* console may be locked down */ }
        _broadcastSessionExpired(reason);
      }
    }
    return Promise.reject(err);
  }
);

export default api;
